import Header from 'components/Header/Header';
import HistoryPredictions from 'components/HistoryPredictions';
import { SITE } from 'config/config';
import { App, Spin } from 'antd';
import dayjs from 'dayjs';
import { useEffect, useState } from 'react';
import { getFP, getIP, getWorldCupHistoryPredictionsAccount } from 'service/base';
import { LocalStorageKey } from 'utils/common/const';
import { twMerge } from 'tailwind-merge';

const HistoryPage = () => {
  const { notification } = App.useApp();
  const [activeHistoryTab, setActiveHistoryTab] = useState('current');
  const [IP, setIP] = useState('');
  const [FP, setFP] = useState('');
  const [history2Rows, setHistory2Rows] = useState([]);
  const [isLoadingHistory2, setIsLoadingHistory2] = useState(false);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const [ipRes, fpRes] = await Promise.all([getIP(), getFP()]);
        if (!mounted) return;
        setIP(ipRes);
        setFP(fpRes);
      } catch (e) {
        // keep empty; component already handles warning on submit
      }
    })();
    return () => {
      mounted = false;
    };
  }, []);

  const tabs = [
    { key: 'current', label: 'DỰ ĐOÁN TỶ SỐ' },
    { key: 'edit', label: 'DỰ ĐOÁN WORLD CUP' }
  ];

  const fetchHistory2 = async (accountValue) => {
    const account = String(accountValue || '').trim();
    if (!account) {
      notification.warning({
        message: 'Cảnh báo',
        description: 'Vui lòng nhập tài khoản để xem lịch sử.'
      });
      return;
    }
    setIsLoadingHistory2(true);
    try {
      const res = await getWorldCupHistoryPredictionsAccount({
        account,
        site: SITE,
        ip: IP?.ip ?? IP,
        fp: FP?.fp ?? FP
      });
      const rows = Array.isArray(res?.data) ? res.data : [];
      setHistory2Rows(rows);
      if (!rows.length) {
        notification.info({
          message: 'Thông báo',
          description: 'Không có dữ liệu lịch sử.'
        });
      }
    } catch (e) {
      notification.error({
        message: 'Lỗi',
        description: e?.message || 'Không tải được lịch sử dự đoán.'
      });
    } finally {
      setIsLoadingHistory2(false);
    }
  };

  useEffect(() => {
    if (activeHistoryTab !== 'edit') return;

    const loadWorldCupHistory = () => {
      if (!localStorage.getItem(LocalStorageKey.ACCESS_TOKEN)) {
        setHistory2Rows([]);
        return;
      }
      const savedAccount = localStorage.getItem('userAccount') || '';
      if (!savedAccount) {
        setHistory2Rows([]);
        return;
      }
      fetchHistory2(savedAccount);
    };

    loadWorldCupHistory();
    window.addEventListener('sc88-auth-change', loadWorldCupHistory);
    return () => window.removeEventListener('sc88-auth-change', loadWorldCupHistory);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeHistoryTab, IP, FP]);

  return (
    <div className={twMerge(SITE, 'w-full h-full')}>
      <img
        src="bg.png"
        alt="Background"
        className="w-full h-full fixed right-0 bottom-0 object-cover object-center"
      />
      <div className="container relative z-10 pb-[24px] md:pb-[32px] px-2">
        <Header />

        <div
          className="mx-auto max-w-[1000px] mt-2 bg-white/95 border-2 border-solid shadow shadow-primary p-4 w-full md:pb-8 md:pt-4 rounded-3xl"
          style={{
            background: '#FFFFFF80',
            borderColor: '#f27121',
            borderRadius: '1.5rem'
          }}
        >
          <div className="flex items-center justify-center gap-3 mt-1 mb-4 flex-wrap">
            {tabs.map((tab) => {
              const isActive = activeHistoryTab === tab.key;
              return (
                <button
                  key={tab.key}
                  type="button"
                  onClick={() => setActiveHistoryTab(tab.key)}
                  className={twMerge(
                    'rounded-full px-4 py-2 text-[13px] md:text-[14px] font-bold border transition-colors',
                    isActive
                      ? 'bg-[#f27121] border-[#f27121] text-white'
                      : 'bg-white border-[#d6dbe3] text-[#334155] hover:border-[#f27121]'
                  )}
                >
                  {tab.label}
                </button>
              );
            })}
          </div>

          {activeHistoryTab === 'current' ? (
            <HistoryPredictions embedded IP={IP} FP={FP} />
          ) : (
            <div className="px-1">
              {isLoadingHistory2 ? (
                <div className="py-10 text-center">
                  <Spin />
                </div>
              ) : (
                <div className="overflow-x-auto rounded-[10px] border border-[#d5d9df]">
                  <table className="w-full min-w-[720px] border-collapse">
                    <thead>
                      <tr className="bg-[#f27120] text-white">
                        <th className="px-3 py-3 text-center text-[14px] font-bold border border-[#9fb5dd]">
                          Thời gian
                        </th>
                        <th className="px-3 py-3 text-center text-[14px] font-bold border border-[#9fb5dd]">
                          Dự đoán
                        </th>
                        <th className="px-3 py-3 text-center text-[14px] font-bold border border-[#9fb5dd]">
                          Bạn dự đoán
                        </th>
                        <th className="px-3 py-3 text-center text-[14px] font-bold border border-[#9fb5dd]">
                          Kết quả
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {history2Rows.length ? (
                        history2Rows.map((row, idx) => (
                          <tr key={`${row.type || 'row'}-${row.name || ''}-${idx}`}>
                            <td className="px-3 py-3 text-center text-[14px] text-[#374151] border border-[#d6dbe3]">
                              {row?.createdAt ? dayjs(row.createdAt).format('DD/MM/YYYY') : '-'}
                            </td>
                            <td className="px-3 py-3 text-center text-[14px] text-[#374151] border border-[#d6dbe3]">
                              {row?.type || '-'}
                            </td>
                            <td className="px-3 py-3 text-center text-[14px] font-semibold text-[#f27120] border border-[#d6dbe3]">
                              {row?.name || '-'}
                            </td>
                            <td className="px-3 py-3 text-center text-[14px] text-[#2f5ca8] border border-[#d6dbe3]">
                              {row?.result > 0 ? row?.result : '-'}
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td
                            colSpan={4}
                            className="px-3 py-8 text-center text-[14px] text-[#6b7280] border border-[#d6dbe3]"
                          >
                            Chưa có dữ liệu lịch sử.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default HistoryPage;
